import { IntegrationServiceDataMap } from '@/app/settings/organization/integrations/utils';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { RectangleStackIcon } from '@heroicons/react/16/solid';
import type { ModelName } from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import type { Icon } from '../detail/components/events/utils';
import { Icons } from '../icons';
import type { IBadgeVariants } from '../ui/badge';

const now = DateTime.now().toISODate();

export interface FilterEnumChoice {
  title: string;
  filters: {
    operator: FilterOperator;
    value: string;
    fieldName?: string;
  }[];
  id?: string;
  overrideExternalFieldName?: string;
  /** Some filter are compounds of other filters, and should not be shown as a result. */
  compound?: boolean;
  badgeVariant: IBadgeVariants;
}

export interface Filter {
  kind: FilterKind;
  title: string;
  externalFieldName: string;
  choices?: { v: string | number | boolean; d: string }[];
  enumChoices?: FilterEnumChoice[];
  relationModelName?: ModelName;
  operators: FilterOperator[];
  defaultOperator: FilterOperator;
  relationDefaultFilters?: JsonFilter;
  icon?: Icon;
  id?: string;
  transformValue?: (value: FilterValue) => Promise<FilterValue> | FilterValue;
  maxSelectedItems?: number;
}

export type FilterValue = string | number | string[] | boolean | null;

interface DefaultFilter {
  kind: FilterKind;
  operators: FilterOperator[];
  defaultOperator: FilterOperator;
  icon?: Icon;
}

export interface CustomFilter {
  readonly filterId: string;
  readonly title: string;
  readonly href: string;
  readonly count: number;
  readonly isLoadingCount: boolean;
  readonly variant: IBadgeVariants;
}

export interface AppliedFilter {
  operator: FilterOperator;
  externalFieldName: string;
  value: FilterValue;
}

export const DRAFT_FILTER_ID = 'contract_draft';
export const PROCESS_STATE_FILTER_ID = 'contract_process_state';

export const RELATION_FILTER = 're' as const;
export const STRING_FILTER = 'st' as const;
export const SELECT_FILTER = 'se' as const;
export const DATE_FILTER = 'dt' as const;
export const BOOLEAN_FILTER = 'bo' as const;
export const NUMBER_FILTER = 'no' as const;
export const TAG_FILTER = 'tg' as const;
export const NULL_FILTER = 'nu' as const;
export const ENUM_FILTER = 'en' as const; // special case for i.e statuses on contracts, always rendered

export const FILTER_KINDS = {
  RELATION_FILTER: RELATION_FILTER,
  STRING_FILTER: STRING_FILTER,
  SELECT_FILTER: SELECT_FILTER,
  DATE_FILTER: DATE_FILTER,
  BOOLEAN_FILTER: BOOLEAN_FILTER,
  NUMBER_FILTER: NUMBER_FILTER,
  TAG_FILTER: TAG_FILTER,
  NULL_FILTER: NULL_FILTER,
  ENUM_FILTER: ENUM_FILTER,
} as const;

export type FilterKind = (typeof FILTER_KINDS)[keyof typeof FILTER_KINDS];

export const FILTER_OPERATOR = [
  '__in',
  '__in!',
  '__exact',
  '__exact!',
  '__iexact',
  '__iexact!',
  '__icontains',
  '__icontains!',
  '__lte',
  '__lte!',
  '__gte',
  '__gte!',
  '__lt',
  '__lt!',
  '__gt',
  '__gt!',
  '__isnull',
  '__isnull!',
  'noop',
  // This is a special operator that doesn't exist in backend, but that frontend uses to filter on a certain day.
  '__date_is',
  '__date_past',
  '__date_future',
  '__date_time_past',
  '__date_time_future',
] as const;

export type FilterOperator = (typeof FILTER_OPERATOR)[number];

export const OPERATORS = {
  DATE_IS: ['__date_is'],
  DATE_PAST: ['__date_past'],
  DATE_FUTURE: ['__date_future'],
  DATE_TIME_PAST: ['__date_time_past'],
  DATE_TIME_FUTURE: ['__date_time_future'],
  IN: ['__in', '__in!'], // in for arrays / booleans
  TEXT_EXACT: ['__iexact', '__iexact!'], // exact for strings
  INTEGER_EXACT: ['noop', '__exact!'], // 'exact' on integers does not work but 'noop' works as you would expect exact to work.
  TEXT_CONTAINS: ['__icontains', '__icontains!'],
  LTE: ['__lte', '__lte!'], // less than or equal for dates/numbers
  GTE: ['__gte', '__gte!'], // greater than or equal for dates/numbers
  LT: ['__lt', '__lt!'], // less than for dates/numbers
  GT: ['__gt', '__gt!'], // greater than for dates/numbers
  ISNULL: ['__isnull', '__isnull!'], // null/not null
  NOOP: ['noop'], // for when we arent allowed to add any operators, no-operation
} as const satisfies Record<string, FilterOperator[]>;

export const getReadableOperator = (
  operator: FilterOperator,
  kind: FilterKind
) => {
  switch (operator) {
    case '__in': {
      if (kind === BOOLEAN_FILTER) {
        return 'är';
      } else {
        return 'innehåller';
      }
    }
    case '__in!': {
      if (kind === BOOLEAN_FILTER) {
        return 'är inte ';
      } else {
        return 'innehåller inte';
      }
    }
    case '__iexact':
    case '__exact':
      return 'är';
    case '__exact!':
    case '__iexact!':
      return 'är inte';
    case '__icontains':
      return 'innehåller';
    case '__icontains!':
      return 'innehåller inte';
    case '__lte': {
      switch (kind) {
        case 'dt':
          return 'innan eller lika med';
        default:
          return 'mindre än eller lika med';
      }
    }
    case '__lte!': {
      switch (kind) {
        case 'dt':
          return 'inte innan eller lika med';
        default:
          return 'inte mindre än eller lika med';
      }
    }
    case '__gte': {
      switch (kind) {
        case 'dt':
          return 'efter eller lika med';
        default:
          return 'mer än eller lika med';
      }
    }
    case '__gte!': {
      switch (kind) {
        case 'dt':
          return 'inte efter eller lika med';
        default:
          return 'inte mer än eller lika med';
      }
    }
    case '__lt': {
      switch (kind) {
        case 'dt':
          return 'innan';
        default:
          return 'mindre än';
      }
    }
    case '__lt!': {
      switch (kind) {
        case 'dt':
          return 'inte innan';
        default:
          return 'inte mindre än';
      }
    }
    case '__gt': {
      switch (kind) {
        case 'dt':
          return 'efter';
        default:
          return 'mer än';
      }
    }
    case '__gt!': {
      switch (kind) {
        case 'dt':
          return 'inte efter';
        default:
          return 'inte mer än';
      }
    }
    case '__isnull':
      return 'har inget värde';
    case '__isnull!':
      return 'har ett värde';
    case 'noop':
      return 'är';
    case '__date_is':
      return 'är';
    case '__date_past':
      return 'senaste';
    case '__date_future':
      return 'kommande';
    case '__date_time_past':
      return 'senaste';
    case '__date_time_future':
      return 'kommande';
  }
};

export const defaultRelationFilter: DefaultFilter = {
  kind: RELATION_FILTER,
  operators: [...OPERATORS.IN, ...OPERATORS.ISNULL],
  defaultOperator: OPERATORS.IN[0],
  icon: Icons.relation,
};

export const defaultStringFilter: DefaultFilter = {
  kind: STRING_FILTER,
  operators: [
    ...OPERATORS.TEXT_CONTAINS,
    ...OPERATORS.TEXT_EXACT,
    ...OPERATORS.ISNULL,
  ],
  defaultOperator: OPERATORS.TEXT_CONTAINS[0],
  icon: Icons.string,
};

export const defaultDateTimeFilter: DefaultFilter = {
  kind: DATE_FILTER,
  operators: [
    OPERATORS.DATE_IS[0],
    OPERATORS.LT[0],
    OPERATORS.GT[0],
    OPERATORS.DATE_TIME_PAST[0],
    OPERATORS.DATE_TIME_FUTURE[0],
  ],
  defaultOperator: OPERATORS.DATE_TIME_FUTURE[0],
  icon: Icons.date,
};

export const defaultDateFilter: DefaultFilter = {
  kind: DATE_FILTER,
  operators: [
    OPERATORS.NOOP[0],
    OPERATORS.LT[0],
    OPERATORS.GT[0],
    OPERATORS.DATE_PAST[0],
    OPERATORS.DATE_FUTURE[0],
  ],
  defaultOperator: OPERATORS.DATE_FUTURE[0],
  icon: Icons.date,
};

export const defaultNumberFilter: DefaultFilter = {
  kind: NUMBER_FILTER,
  operators: [
    ...OPERATORS.INTEGER_EXACT,
    ...OPERATORS.LTE,
    ...OPERATORS.GTE,
    ...OPERATORS.LT,
    ...OPERATORS.GT,
  ],
  defaultOperator: OPERATORS.INTEGER_EXACT[0],
  icon: Icons.number,
};

export const defaultBooleanFilter: DefaultFilter = {
  kind: BOOLEAN_FILTER,
  operators: [...OPERATORS.IN],
  defaultOperator: OPERATORS.IN[0],
  icon: Icons.boolean,
};
export const defaultNullFilter: DefaultFilter = {
  kind: NULL_FILTER,
  operators: [...OPERATORS.ISNULL],
  defaultOperator: OPERATORS.ISNULL[1],
  icon: Icons.boolean,
};

export const defaultTagFilter: DefaultFilter = {
  kind: TAG_FILTER,
  operators: OPERATORS.NOOP,
  defaultOperator: OPERATORS.NOOP[0],
};

export const defaultSelectFilter: DefaultFilter = {
  kind: SELECT_FILTER,
  operators: [...OPERATORS.IN, ...OPERATORS.TEXT_EXACT],
  defaultOperator: OPERATORS.IN[0],
  icon: RectangleStackIcon,
};

export const defaultEnumFilter: DefaultFilter = {
  kind: ENUM_FILTER,
  operators: OPERATORS.IN,
  defaultOperator: OPERATORS.IN[0],
};

export const customFilterConfig = {
  custom_invoice_filter: {
    credit_rental_invoices: 'rental_invoice=true&total_value__lte=-1',
    credit_invoices: 'rental_invoice=false&total_value__lte=-1',
    not_attested: `attested_date__isnull=true`,
    failed_attested: `attested_date__isnull=false&ocr_number__isnull=true`,
    reminders: `attested_date__isnull=false&closed_time__isnull=true&debt_invoice_counts_kind_0__gt=0`,
    reminderspending: `attested_date__isnull=false&closed_time__isnull=true&due_date__lt=${DateTime.now().toISODate()}&reminder_amount__gte=1&automatic_debt_invoicing_paused=false`,
    debt: `attested_date__isnull=false&closed_time__isnull=true&debt_invoice_counts_kind_1__gt=0`,
    overdue: `attested_date__isnull=false&closed_time__isnull=true&due_date__lt=${DateTime.now().toISODate()}`,
    fully_paid: `attested_date__isnull=false&closed_time__isnull=false`,
    // overdue_not_handled: `attested_date__isnull=false&closed_time__isnull=true&reminder_amount=0&due_date__lt=${DateTime.now().toISODate()}`,
    open: `attested_date__isnull=false&closed_time__isnull=true`,
    not_sent: `attested_date__isnull=false&closed_time__isnull=true&due_date__gte=${DateTime.now().toISODate()}&distribution_date__isnull=true`,
    fully_paid_with_open_debt: `attested_date__isnull=false&closed_time__isnull=false&unclosed_debt_invoices__gt=0`,
  },
  custom_component_filter: {
    active: `uninstallation_time__isnull=true`,
    upcoming_inactive: `uninstallation_time__gt=${DateTime.now().toUTC().toISO()}`,
    inactive: `uninstallation_time__lte=${DateTime.now().toUTC().toISO()}`,
  },
  custom_contract_filter: {
    sub_lease_active:
      'subtenant_connection_status__in=3,4&subtenant_connection_stakeholder_type__in=0',
    guarantor_active:
      'subtenant_connection_status__in=3,4&subtenant_connection_stakeholder_type__in=1',
    renegotiation_process:
      'pending_re_negotiations__in=true&cancellation_signed_time__isnull=true',
  },
  custom_space_status_filter: {
    vacant_no_future_contract: 'status__in=5,6,12&future_contracting__in=0',
  },
  custom_debt_invoice_filter: {
    not_attested: `attested_date__isnull=true`,
    reminders: `kind=0`,
    debt: `kind=1`,
    overdue: `attested_date__isnull=false&closed_time__isnull=true&due_date__lt=${DateTime.now().toISODate()}`,
  },
  custom_verification_group_filter: {
    attested: `confirmation_date__isnull=false`,
    not_attested: `confirmation_date__isnull=true`,
  },
  custom_verification_filter: {
    fortnox_exported: `connected_integration_applications=${IntegrationServiceDataMap.FORTNOX?.applicationId}`,
    fortnox_not_exported: `verification_group__isnull=false&integration_applications_not_connected=${IntegrationServiceDataMap.FORTNOX?.applicationId}`,
    visma_exported: `connected_integration_applications=${IntegrationServiceDataMap.VISMA_EEKONOMI?.applicationId}`,
    visma_not_exported: `verification_group__isnull=false&integration_applications_not_connected=${IntegrationServiceDataMap.VISMA_EEKONOMI?.applicationId}`,
    file_exported: `exported_for_import_in_file__isnull=false`,
    file_not_exported: `exported_for_import_in_file__isnull=true`,
    fortnox_and_file_not_exported: `verification_group__isnull=false&integration_applications_not_connected=${IntegrationServiceDataMap.FORTNOX?.applicationId}&exported_for_import_in_file__isnull=true`,
    visma_and_file_not_exported: `verification_group__isnull=false&integration_applications_not_connected=${IntegrationServiceDataMap.VISMA_EEKONOMI?.applicationId}&exported_for_import_in_file__isnull=true`,
    imported_to_pigello: `verification_group__isnull=true`,
  },
  custom_signable_doc_filter: {
    initiated_not_started: `signing_initiated=true&signing_started_time__isnull=true`,
    not_initiated: `signing_initiated=false&external_sign_engine__isnull=false`,
    manual: `signing_initiated=false&external_sign_engine__isnull=true`,
    rejected_last_2_weeks: `rejected_time__lte!=${DateTime.now().minus({ weeks: 2 }).toISODate()}&rejected_time__isnull=false`,
    aborted_last_2_weeks: `signing_aborted_time__lte!=${DateTime.now().minus({ weeks: 2 }).toISODate()}&signing_aborted_time__isnull=false`,
  },
  custom_sensor_filter: {
    not_matched: `apartment__isnull=true&parking_spot__isnull=true&industrial_premises__isnull=true&common_area__isnull=true`,
  },
  custom_payments_filter: {
    distributed: `invoice__isnull=false&annulled=false`,
    not_handled: `is_unhandled=true&annulled=false`,
    not_matched: `is_unmatched=true&annulled=false`,
    annulled: `annulled=true`,
    overpayment: `is_overpayment=true&annulled=false`,
    matched_manual: `matched_date__isnull=true&invoice__isnull=true&annulled=false&original_invoice__isnull=false`,
  },
  custom_vacancyinvoice_filter: {
    attested: `attested_date__isnull=false`,
    not_attested: `attested_date__isnull=true`,
    annulled: `annulled=true`,
  },
  custom_generalerrand_filter: {
    delayed: `status__in!=4,5,6&planned_done_time__lt=${DateTime.now().toISODate()}`,
  },
  custom_roundingoccurrence_filter: {
    delayed: `status__in!=4&planned_start_time__lt=${DateTime.now().toISODate()}`,
  },
  custom_sendout_filter: {
    pinned_to: `pinned_to__gte=${DateTime.now().toUTC().toISO()}`,
  },
  custom_sendout_status_filter: {
    sent: `sent_at__lt=${DateTime.now().toISODate()}`,
    scheduled: `scheduled_send__gt=${DateTime.now().toISODate()}&is_draft=false&sent_at__isnull=true`,
    draft: 'is_draft=true&scheduled_send__isnull=true',
  },
  custom_revenue_increase_filter: {
    merged: `merged_revenue_row__isnull=false`,
    not_merged: `merged_revenue_row__isnull=true`,
  },
  custom_apartment_listing_integration_filter: {
    no_connections: `integration_applications_not_connected=${IntegrationServiceDataMap.UPPSALA_BOSTADSFORMEDLING?.applicationId},${IntegrationServiceDataMap.HOMEQ?.applicationId}`,
    uppsala_connected: `connected_integration_applications=${IntegrationServiceDataMap.UPPSALA_BOSTADSFORMEDLING?.applicationId}`,
    uppsala_connected_pending: `pending_integration_applications=${IntegrationServiceDataMap.UPPSALA_BOSTADSFORMEDLING?.applicationId}&status=PUBLISHED&closed_at__isnull=true&`,
    homeq_connected: `connected_integration_applications=${IntegrationServiceDataMap.HOMEQ?.applicationId}`,
  },
  custom_apartment_check_active_listing: {
    no_active_listing: `has_active_listing=false`,
    has_active_listing: `has_active_listing=true`,
  },
  custom_vacancy_contract_status_filter: {
    active: `start_date__lte=${now}&end_date__lt!=${now}`,
    earlier: `end_date__lt=${now}`,
    upcoming: `start_date__gt=${now}`,
  },
};

export const DATE_WINDOW_PRESETS = {
  '1': { title: '1 dag', value: 1 },
  '2': { title: '2 dagar', value: 2 },
  '3': { title: '3 dagar', value: 3 },
  '5': { title: '5 dagar', value: 5 },
  '7': { title: '1 vecka', value: 7 },
  '14': { title: '2 veckor', value: 14 },
  '21': { title: '3 veckor', value: 21 },
  '30': { title: '1 månad', value: 30 },
  '60': { title: '2 månader', value: 60 },
  '90': { title: '3 månader', value: 90 },
  '180': { title: '6 månader', value: 180 },
  '365': { title: '1 år', value: 365 },
} as const;
